<template>
  <div class="stripe-payment-form">
    <b-form-group label="Payment Information:" label-for="payment-element">
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner small></b-spinner>
        <span class="ml-2">Loading payment form...</span>
      </div>
      <div id="payment-element" v-show="!isLoading && !cardError">
        <!-- Stripe Payment Element will be mounted here -->
        <!-- Includes built-in "Save for future use" checkbox and Apple/Google Pay buttons -->
      </div>
      <div v-if="cardError" class="text-danger mt-2">
        {{ cardError }}
      </div>
    </b-form-group>

    <div v-if="isProcessing" class="text-info mt-2">
      <b-spinner small></b-spinner>
      <span class="ml-2">Processing payment...</span>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

export default {
  name: "StripePaymentForm",
  props: {
    amount: {
      type: Number,
      required: true,
    },
    useSetupIntent: {
      type: Boolean,
      required: false,
      default: false,
    },
    customerEmail: {
      type: String,
      required: false,
    },
    customerName: {
      type: String,
      required: false,
    },
    existingCustomerId: {
      type: String,
      required: false,
      default: null,
    },
    autoMount: {
      type: Boolean,
      required: false,
      default: true, // Default to true for backward compatibility
    },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      cardError: null,
      isProcessing: false,
      isLoading: true,
      clientSecret: null,
      customerId: null,
      setupIntentId: null, // Store setup intent ID when using Setup Intent
    };
  },
  async mounted() {
    // Only auto-initialize if autoMount prop is true
    if (this.autoMount) {
      await this.initializeStripe();
    } else {
      // If not auto-mounting, set loading to false
      this.isLoading = false;
    }
  },
  watch: {
    async amount(newAmount, oldAmount) {
      // Skip if amount hasn't actually changed
      if (newAmount === oldAmount) {
        console.log("💰 Amount watcher fired but amount unchanged:", newAmount);
        return;
      }

      console.log(
        `💰 Amount changed from $${(oldAmount / 100).toFixed(2)} to $${(
          newAmount / 100
        ).toFixed(2)}`
      );
      console.log("🔑 Client secret exists:", !!this.clientSecret);
      console.log(
        "🔑 Client secret value:",
        this.clientSecret ? this.clientSecret.substring(0, 20) + "..." : "null"
      );

      // If we already have a payment intent, update it
      if (this.clientSecret && newAmount > 0) {
        console.log(
          "✅ Calling updatePaymentIntent with new amount:",
          newAmount
        );
        await this.updatePaymentIntent(newAmount);
      }
      // If no payment intent yet and amount is valid, initialize
      else if (newAmount > 0 && !this.clientSecret) {
        console.log(
          "🆕 No payment intent yet, initializing with amount:",
          newAmount
        );
        this.initializeStripe();
      } else {
        console.log(
          "⚠️ Cannot update: clientSecret:",
          !!this.clientSecret,
          "newAmount:",
          newAmount
        );
      }
    },
    async existingCustomerId(newCustomerId, oldCustomerId) {
      // If customer ID changes and we have an active payment session, clear it
      // This prevents "customer mismatch" errors when switching between customers
      if (this.clientSecret && newCustomerId !== oldCustomerId) {
        console.log(
          "👤 Customer changed from",
          oldCustomerId || "none",
          "to",
          newCustomerId || "none"
        );
        console.log("🗑️ Clearing Stripe session due to customer change");
        console.log(
          "🔑 Current clientSecret:",
          this.clientSecret?.substring(0, 20) + "..."
        );

        await this.clearStripeSession();

        // Reset the payment initialization flag in parent component
        this.$parent.stripePaymentInitialized = false;

        console.log("✅ Stripe session cleared, ready for new customer");
      }
    },
    // The payment mode switched between "charge now" (Payment Intent) and
    // "save card / enroll-only" (Setup Intent). The intent type is fixed when
    // it is created, so we must tear down the existing intent (this cancels a
    // Payment Intent; a Setup Intent is abandoned and expires on its own) and
    // rebuild with the correct type. Without this, unticking enroll-only left a
    // Setup Intent in place (no charge taken despite a non-zero total), and
    // ticking it left a stale Payment Intent that was never cancelled.
    async useSetupIntent(newVal, oldVal) {
      if (newVal === oldVal) return;
      // Only rebuild if an intent has already been created. If the form hasn't
      // initialized yet, the normal init path will read the current mode and
      // create the correct intent type on its own.
      if (!this.clientSecret) return;
      console.log(
        `🔁 Payment mode changed (useSetupIntent: ${oldVal} → ${newVal}). Rebuilding intent.`
      );
      await this.clearStripeSession();
      // initializeStripe() self-guards: Setup Intent allows amount 0, Payment
      // Intent requires amount > 0 (otherwise it no-ops and clears loading).
      await this.initializeStripe();
    },
  },
  methods: {
    // Clears all Stripe-related fields and destroys Stripe elements
    async clearStripeSession() {
      // Cancel the payment/setup intent on the backend if one exists
      if (this.clientSecret) {
        try {
          const intentId = this.clientSecret.split("_secret_")[0];

          // Only cancel payment intents (pi_...), not setup intents (seti_...)
          // Setup intents can't be cancelled once created, they just expire
          if (intentId.startsWith("pi_")) {
            console.log("🗑️ Cancelling payment intent:", intentId);
            await this.$http.post(
              `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/cancel-payment-intent`,
              {
                paymentIntentId: intentId,
              },
              {
                headers: {
                  "Request-Token": process.env.VUE_APP_SEC_HEADER,
                  "Access-Control-Allow-Origin": "*",
                  "Content-Type": "application/json",
                },
              }
            );
            console.log("✅ Payment intent cancelled successfully");
          } else if (intentId.startsWith("seti_")) {
            console.log(
              "ℹ️ Setup intent will expire automatically, no need to cancel:",
              intentId
            );
          }
        } catch (error) {
          // Check if error is because payment already succeeded
          const errorMessage =
            error.response?.data?.message || error.message || "";
          if (
            errorMessage.includes("already been succeeded") ||
            errorMessage.includes("already succeeded")
          ) {
            console.log(
              "ℹ️ Payment intent already succeeded, no need to cancel"
            );
          } else {
            console.error("❌ Failed to cancel payment intent:", error);
          }
          // Don't throw - we'll clear the session anyway
        }
      }

      // Clear frontend state - check if elements exist before destroying
      try {
        if (
          this.paymentElement &&
          typeof this.paymentElement.destroy === "function"
        ) {
          this.paymentElement.destroy();
        }
      } catch (error) {
        console.warn(
          "Payment element already destroyed or not initialized:",
          error.message
        );
      }

      this.paymentElement = null;
      this.stripe = null;
      this.elements = null;
      this.cardError = null;
      this.isProcessing = false;
      this.isLoading = true;
      this.clientSecret = null;
      this.customerId = null;
      this.setupIntentId = null;
    },
    async initializeStripe() {
      // Don't initialize if amount is invalid (negative) for Payment Intent
      // But allow amount=0 for Setup Intent
      if (!this.useSetupIntent && (!this.amount || this.amount <= 0)) {
        this.isLoading = false;
        return;
      }

      try {
        this.isLoading = true;
        this.cardError = null;

        // Load Stripe
        this.stripe = await loadStripe(
          process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
        );

        if (!this.stripe) {
          console.error("Stripe failed to load");
          this.cardError =
            "Failed to load payment form. Please refresh the page.";
          this.isLoading = false;
          return;
        }

        // Create either Setup Intent (save payment method only) or Payment Intent (charge now)
        let clientSecret, customerId, setupIntentId;

        if (this.useSetupIntent) {
          // Setup Intent: Save payment method without charging
          console.log(
            "🔧 Using Setup Intent mode (save payment method, no charge)"
          );
          const setupResponse = await this.createSetupIntent();
          clientSecret = setupResponse.clientSecret;
          customerId = setupResponse.customerId;
          setupIntentId = setupResponse.setupIntentId;
          this.setupIntentId = setupIntentId;
        } else {
          // Payment Intent: Charge the customer now
          console.log("💳 Using Payment Intent mode (charge customer)");
          const paymentResponse = await this.createPaymentIntent();
          clientSecret = paymentResponse.clientSecret;
          customerId = paymentResponse.customerId;
        }

        this.clientSecret = clientSecret;
        this.customerId = customerId;

        if (!clientSecret) {
          throw new Error("No client secret returned from backend");
        }

        // Initialize Elements configuration with theme-aware colors
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        const elementsConfig = {
          clientSecret: clientSecret,
          appearance: {
            theme: isDark ? "night" : "stripe",
            variables: {
              colorPrimary: "#0570de",
              colorBackground: isDark ? "#1f2937" : "#ffffff",
              colorText: isDark ? "#f9fafb" : "#32325d",
              colorDanger: "#fa755a",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              spacingUnit: "4px",
              borderRadius: "4px",
            },
          },
        };

        // Create Customer Session for ALL customers (new or existing)
        // This enables the "Save for future use" checkbox in Payment Element
        const customerIdForSession = customerId || this.existingCustomerId;
        if (customerIdForSession) {
          console.log(
            "Creating Customer Session for customer:",
            customerIdForSession
          );

          try {
            const customerSession = await this.createCustomerSession(
              customerIdForSession
            );

            if (customerSession && customerSession.clientSecret) {
              console.log("Customer Session created successfully");

              // Add customer session to elements config
              elementsConfig.customerSessionClientSecret =
                customerSession.clientSecret;

              console.log(
                'Payment Element will show "Save for future use" checkbox'
              );
            }
          } catch (error) {
            console.warn(
              "Failed to create Customer Session, continuing without save option:",
              error
            );
            // Continue without customer session - payment will still work, just no save checkbox
          }
        }

        // Create Stripe Elements with client secret (and customer session if available)
        this.elements = this.stripe.elements(elementsConfig);

        // Create Payment Element (shows saved cards if available)
        this.paymentElement = this.elements.create("payment", {
          layout: "tabs",
          wallets: {
            applePay: "never", // Don't show Apple Pay
            googlePay: "never", // Don't show Google Pay
          },
        });

        // Mount the payment element
        this.paymentElement.mount("#payment-element");

        // Listen for payment element changes
        this.paymentElement.on("change", (event) => {
          if (event.error) {
            this.cardError = event.error.message;
          } else {
            this.cardError = null;
          }
          // Emit payment completion status to parent
          this.$emit("card-complete", event.complete);
        });

        this.isLoading = false;
      } catch (error) {
        console.error("Error initializing Stripe:", error);
        this.cardError =
          "Failed to initialize payment form. The backend may need to be updated to support Payment Element. Error: " +
          (error.message || "Unknown error");
        this.isLoading = false;
      }
    },

    async createPaymentIntent() {
      try {
        console.log("🔍 createPaymentIntent called with:");
        console.log("  - amount:", this.amount);
        console.log("  - email:", this.customerEmail);
        console.log("  - name:", this.customerName);
        console.log("  - existingCustomerId prop:", this.existingCustomerId);
        console.log(
          "  - existingCustomerId type:",
          typeof this.existingCustomerId
        );

        const requestBody = {
          amount: this.amount,
          email: this.customerEmail,
          name: this.customerName,
          savePaymentMethod: true,
          setAsDefault: true,
          customerId: this.existingCustomerId || null,
        };

        console.log(
          "📤 Sending to backend:",
          JSON.stringify(requestBody, null, 2)
        );

        // Call backend API to create payment intent
        // Backend should:
        // 1. Create/find a Stripe customer and attach it to the payment intent
        // 2. Set setup_future_usage: 'off_session' to enable saving (Payment Element shows checkbox)
        // 3. After payment succeeds, set the payment method as default (via webhook or inline)
        const response = await this.$http.post(
          `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/create-payment-intent`,
          requestBody,
          {
            headers: {
              "Request-Token": process.env.VUE_APP_SEC_HEADER,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        );

        console.log(
          "📥 Backend response:",
          JSON.stringify(response.data, null, 2)
        );
        console.log("📥 Payment Intent customer ID:", response.data.customerId);
        console.log(
          "📥 Client secret starts with:",
          response.data.clientSecret?.substring(0, 20)
        );

        return response.data;
      } catch (error) {
        console.error("❌ Error creating payment intent:", error);
        console.error("❌ Error response:", error.response?.data);
        throw new Error("Failed to create payment intent");
      }
    },

    async createSetupIntent() {
      try {
        // Call backend API to create setup intent
        // Setup Intent is used when we want to save payment method WITHOUT charging now
        // This is for "funds collected locally" + "auto-renew" scenario
        console.log("Creating Setup Intent for payment method collection");

        const response = await this.$http.post(
          `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/create-setup-intent`,
          {
            email: this.customerEmail,
            name: this.customerName,
            customerId: this.existingCustomerId || null,
          },
          {
            headers: {
              "Request-Token": process.env.VUE_APP_SEC_HEADER,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Setup Intent created:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error creating setup intent:", error);
        throw new Error("Failed to create setup intent");
      }
    },

    async updatePaymentIntent(newAmount) {
      try {
        console.log("🔄 Updating payment intent amount to:", newAmount);

        // Extract intent ID from client secret
        const paymentIntentId = this.clientSecret.split("_secret_")[0];
        console.log("🆔 Payment Intent ID:", paymentIntentId);

        // Setup Intents (seti_...) have no amount to update. Sending one to the
        // update-payment-intent endpoint causes a "Payment intent not found"
        // error, so skip the update entirely when we're in setup-intent mode.
        if (this.setupIntentId || paymentIntentId.startsWith("seti_")) {
          console.log(
            "↩️ Skipping amount update: using a Setup Intent (no amount to update)."
          );
          return;
        }

        // Build the full API URL
        const apiUrl = `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/update-payment-intent`;
        console.log("🌐 API URL:", apiUrl);
        console.log("🌐 VUE_APP_APIURL:", process.env.VUE_APP_APIURL);
        console.log("🌐 VUE_APP_APIVER:", process.env.VUE_APP_APIVER);

        // Call backend API to update payment intent amount
        const response = await this.$http.post(
          apiUrl,
          {
            paymentIntentId: paymentIntentId,
            amount: newAmount,
          },
          {
            headers: {
              "Request-Token": process.env.VUE_APP_SEC_HEADER,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        );

        console.log(
          "✅ Payment intent amount updated successfully:",
          response.data
        );
        return response.data;
      } catch (error) {
        console.error("❌ Error updating payment intent:", error);
        console.error("❌ Error response:", error.response?.data);
        console.error("❌ Error status:", error.response?.status);
        // Don't throw - just log the error. The old amount will be used.
        // Alternatively, could reinitialize with new amount
        this.cardError =
          "Failed to update payment amount. Please refresh the page.";
      }
    },

    async createCustomerSession(customerId) {
      try {
        // Call backend API to create Customer Session (ephemeral key)
        // This allows showing saved payment methods for returning customers
        const response = await this.$http.post(
          `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/create-customer-session`,
          {
            stripe_customer_id:
              typeof customerId === "object" ? customerId.id : customerId,
          },
          {
            headers: {
              "Request-Token": process.env.VUE_APP_SEC_HEADER,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        );

        return response.data;
      } catch (error) {
        console.error("Error creating customer session:", error);
        throw new Error("Failed to create customer session");
      }
    },

    async processPayment() {
      // Route to appropriate method based on intent type
      if (this.useSetupIntent) {
        console.log("Routing to confirmSetup (Setup Intent mode)");
        return await this.confirmSetup();
      }

      // Otherwise, process as Payment Intent (charge customer)
      console.log("Processing as Payment Intent (charge customer)");

      if (!this.stripe || !this.paymentElement) {
        this.cardError =
          "Payment form not initialized. Please refresh and try again.";
        return { success: false, error: "Payment form not initialized" };
      }

      if (!this.clientSecret) {
        this.cardError =
          "No payment intent found. Please refresh and try again.";
        return { success: false, error: "No payment intent found" };
      }

      this.isProcessing = true;
      this.cardError = null;

      try {
        // Submit the payment element to validate
        const { error: submitError } = await this.elements.submit();

        if (submitError) {
          this.cardError = submitError.message;
          this.isProcessing = false;
          return {
            success: false,
            error: submitError.message,
          };
        }

        // Confirm payment with Stripe using Payment Element
        // Note: We already have the payment intent from initialization
        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href, // Required but won't redirect
            payment_method_data: {
              billing_details: {
                name: this.customerName,
                email: this.customerEmail,
              },
            },
          },
          redirect: "if_required", // Only redirect if necessary (e.g., 3D Secure)
        });

        if (error) {
          this.cardError = this.getFriendlyErrorMessage(error);
          this.isProcessing = false;
          return {
            success: false,
            error: this.cardError,
          };
        }

        // Extract card details from payment method
        let cardBrand = "Unknown";
        let cardLast4 = "";
        let expMonth = null;
        let expYear = null;

        if (paymentIntent.payment_method) {
          // If payment_method is a string ID, we need to retrieve it
          if (typeof paymentIntent.payment_method === "string") {
            try {
              // Note: Retrieving payment method requires server-side call
              // For now, we'll just use the ID. The backend should retrieve full details.
              console.log("Payment method ID:", paymentIntent.payment_method);
              // Card details will need to be retrieved server-side or from charges
            } catch (err) {
              console.error("Could not retrieve payment method details:", err);
            }
          } else if (paymentIntent.payment_method.card) {
            // If it's expanded, extract card details
            cardBrand = paymentIntent.payment_method.card.brand || "Unknown";
            cardLast4 = paymentIntent.payment_method.card.last4 || "";
            expMonth = paymentIntent.payment_method.card.exp_month || null;
            expYear = paymentIntent.payment_method.card.exp_year || null;
          }
        }

        // Try to get card details from charges if available
        if (
          paymentIntent.charges &&
          paymentIntent.charges.data &&
          paymentIntent.charges.data.length > 0
        ) {
          const charge = paymentIntent.charges.data[0];
          if (
            charge.payment_method_details &&
            charge.payment_method_details.card
          ) {
            cardBrand = charge.payment_method_details.card.brand || cardBrand;
            cardLast4 = charge.payment_method_details.card.last4 || cardLast4;
            expMonth = charge.payment_method_details.card.exp_month || expMonth;
            expYear = charge.payment_method_details.card.exp_year || expYear;
          }
        }

        // Normalize card brand to match Salesforce expectations
        const normalizedBrand = this.normalizeCardBrand(cardBrand);

        // Set payment method as default on the customer
        // Note: Payment method is only saved if user checked the "Save for future use" box in Payment Element
        if (paymentIntent.payment_method && this.customerId) {
          await this.setDefaultPaymentMethod(
            paymentIntent.payment_method,
            this.customerId
          );
        }

        // Payment succeeded
        this.isProcessing = false;
        return {
          success: true,
          paymentIntentId: paymentIntent.id,
          paymentMethodId: paymentIntent.payment_method, // Payment method attached to customer
          transId: paymentIntent.id, // Use payment intent ID as transaction ID
          status: paymentIntent.status,
          cardBrand: normalizedBrand,
          cardLast4: cardLast4,
          expMonth: expMonth,
          expYear: expYear,
          customerId: this.customerId, // Include Stripe customer ID
        };
      } catch (error) {
        console.error("Error processing payment:", error);
        this.cardError =
          error.message || "An error occurred while processing your payment";
        this.isProcessing = false;
        return {
          success: false,
          error: error.message || "Payment processing failed",
        };
      }
    },

    async confirmSetup() {
      // Process Setup Intent (save payment method without charging)
      if (!this.stripe || !this.paymentElement) {
        this.cardError =
          "Payment form not initialized. Please refresh and try again.";
        return { success: false, error: "Payment form not initialized" };
      }

      if (!this.clientSecret) {
        this.cardError = "No setup intent found. Please refresh and try again.";
        return { success: false, error: "No setup intent found" };
      }

      this.isProcessing = true;
      this.cardError = null;

      try {
        // Submit the payment element to validate
        const { error: submitError } = await this.elements.submit();

        if (submitError) {
          this.cardError = submitError.message;
          this.isProcessing = false;
          return {
            success: false,
            error: submitError.message,
          };
        }

        // Confirm setup with Stripe using Payment Element
        console.log("Confirming Setup Intent (saving payment method)");
        const { error, setupIntent } = await this.stripe.confirmSetup({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href, // Required but won't redirect
            payment_method_data: {
              billing_details: {
                name: this.customerName,
                email: this.customerEmail,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          this.cardError = this.getFriendlyErrorMessage(error);
          this.isProcessing = false;
          return {
            success: false,
            error: this.cardError,
          };
        }

        console.log("Setup Intent confirmed successfully:", setupIntent);

        // Extract card details from payment method if available
        let cardBrand = "Unknown";
        let cardLast4 = "";
        let expMonth = null;
        let expYear = null;

        if (setupIntent.payment_method) {
          if (typeof setupIntent.payment_method === "string") {
            console.log("Payment method ID:", setupIntent.payment_method);
          } else if (setupIntent.payment_method.card) {
            cardBrand = setupIntent.payment_method.card.brand || "Unknown";
            cardLast4 = setupIntent.payment_method.card.last4 || "";
            expMonth = setupIntent.payment_method.card.exp_month || null;
            expYear = setupIntent.payment_method.card.exp_year || null;
          }
        }

        // Normalize card brand
        const normalizedBrand = this.normalizeCardBrand(cardBrand);

        // Set payment method as default on the customer
        if (setupIntent.payment_method && this.customerId) {
          await this.setDefaultPaymentMethod(
            setupIntent.payment_method,
            this.customerId
          );
        }

        // Setup completed successfully
        this.isProcessing = false;
        return {
          success: true,
          setupIntentId: setupIntent.id,
          paymentMethodId: setupIntent.payment_method,
          status: setupIntent.status,
          cardBrand: normalizedBrand,
          cardLast4: cardLast4,
          expMonth: expMonth,
          expYear: expYear,
          customerId: this.customerId,
        };
      } catch (error) {
        console.error("Error confirming setup:", error);
        this.cardError =
          error.message || "An error occurred while saving your payment method";
        this.isProcessing = false;
        return {
          success: false,
          error: error.message || "Setup confirmation failed",
        };
      }
    },

    async setDefaultPaymentMethod(paymentMethodId, customerId) {
      try {
        // Call backend to set this payment method as default for the customer
        await this.$http.post(
          `${process.env.VUE_APP_APIURL}/${process.env.VUE_APP_APIVER}/stripe/set-default-payment-method`,
          {
            paymentMethodId: paymentMethodId,
            customerId:
              typeof customerId === "object" ? customerId.id : customerId,
          },
          {
            headers: {
              "Request-Token": process.env.VUE_APP_SEC_HEADER,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json",
            },
          }
        );
        console.log("Payment method set as default successfully");
      } catch (error) {
        // Don't fail the payment if setting default fails
        // Just log the error for monitoring
        console.error("Failed to set payment method as default:", error);
        // Could also emit an event or show a warning to the user if needed
      }
    },

    getFriendlyErrorMessage(error) {
      const declineCode = error.decline_code || error.code;
      const FALLBACK =
        "We're unable to process your payment at this time. Please contact your bank or try a different payment method.";

      // Group decline codes by shared message
      const messageGroups = [
        {
          codes: ["card_not_supported", "currency_not_supported", "transaction_not_allowed", "call_issuer", "card_velocity_exceeded", "invalid_amount"],
          message: "Your card issuer has blocked this transaction for one of many reasons. Please try a different card or contact your bank for more information.",
        },
        {
          codes: ["insufficient_funds"],
          message: "The payment couldn't be completed due to insufficient funds. Please use a different payment method or contact your bank for more information.",
        },
        {
          codes: ["expired_card"],
          message: "This card has expired. Please use a different payment method or update your payment details.",
        },
        {
          codes: ["incorrect_number", "invalid_number", "incorrect_cvc", "invalid_cvc", "incorrect_expiry", "invalid_expiry_month", "invalid_expiry_year", "invalid_pin", "incorrect_pin"],
          message: "The card information entered appears to be incorrect. Please double-check the card number, expiration date, and security code and try again or use another payment method.",
        },
        {
          codes: ["processing_error", "issuer_not_available", "reenter_transaction"],
          message: "We're unable to process your payment right now due to a temporary issue. Please try again in a few minutes or use a different payment method.",
        },
        {
          codes: ["card_declined", "do_not_honor", "generic_decline", "lost_card", "merchant_blacklist", "restricted_card", "service_not_allowed", "stolen_card"],
          message: "Your bank declined this transaction. Please contact your bank or try a different payment method.",
        },
        {
          codes: ["fraudulent", "pickup_card", "security_violation"],
          message: "This payment was declined for security reasons. Please contact your bank or try a different payment method.",
        },
        {
          codes: ["incorrect_zip", "incorrect_address"],
          message: "The billing ZIP/postal code or address doesn't match the card on file. Please verify your billing information and try again.",
        },
        {
          codes: ["authentication_required", "authentication_not_handled", "mobile_device_authentication_required"],
          message: "Additional verification is required to complete this payment. Please follow the prompts from your bank to continue.",
        },
        {
          codes: ["rate_limit"],
          message: "Too many attempts were made with this card. Please wait a few minutes before trying again or use a different payment method.",
        },
        {
          codes: ["amount_too_large"],
          message: "This transaction exceeds your card's limit. Please contact your bank or use a different payment method.",
        },
        {
          codes: ["duplicate_transaction"],
          message: "It looks like this payment was already submitted. Please check your account or contact our Customer Care Center at 800-4-SEATOW if you need assistance.",
        },
        {
          codes: ["invalid_account", "new_account_information_available"],
          message: "We're unable to process this payment with the provided account details. Please verify your information or use a different payment method.",
        },
        {
          codes: ["no_action_taken", "not_permitted", "revocation_of_all_authorizations", "revocation_of_authorization", "stop_payment_order", "withdrawal_count_limit_exceeded"],
          message: FALLBACK,
        },
        {
          codes: ["testmode_decline"],
          message: "A Stripe test card was used. Please use a genuine card.",
        },
      ];

      const match = messageGroups.find((group) => group.codes.includes(declineCode));
      return match ? match.message : FALLBACK;
    },

    normalizeCardBrand(brand) {
      // Normalize Stripe card brands to common names
      const brandMap = {
        visa: "Visa",
        mastercard: "Mastercard",
        amex: "American Express",
        discover: "Discover",
        diners: "Diners Club",
        jcb: "JCB",
        unionpay: "UnionPay",
        unknown: "Unknown",
      };

      return brandMap[brand.toLowerCase()] || brand;
    },

    // Method to validate payment before submission
    validateCard() {
      if (!this.paymentElement) {
        return false;
      }
      return !this.cardError;
    },
  },
  beforeDestroy() {
    // Clean up Stripe elements
    try {
      if (
        this.paymentElement &&
        typeof this.paymentElement.destroy === "function"
      ) {
        this.paymentElement.destroy();
      }
    } catch (error) {
      // Element already destroyed or not initialized - this is fine
      console.warn("Payment element cleanup in beforeDestroy:", error.message);
    }
  },
};
</script>

<style scoped>
#payment-element {
  /* Payment Element handles its own styling via appearance API */
  min-height: 40px;
  margin-bottom: 10px;
}

.stripe-payment-form {
  width: 100%;
}
</style>
